import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { localToday } from '../../utils/dateUtils';
import './saude.css';
import Sidebar from '../../components/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faPencil, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { useInternals } from '../../context/internalContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import { useUsers } from '../../context/usersContext';
import { useUnits } from '../../context/unitsContext';
import { toast } from 'react-toastify';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import PageHeader from '../../components/PageHeader';
import SearchInternal from '../../components/SearchInternal/SearchInternal';
import InternalDataSkeleton from '../../components/InternalDataSkeleton';
import Modal from '../../components/modal';
import useSubmitLock from '../../utils/useSubmitLock';

// Função para inicializar o estado do formulário
const initializeFormData = (userInfo, effectiveUnitId) => ({
  internal: '',
  date: localToday(),
  dentist: '',
  dentist_cro: '',
  assistant: '',
  assistant_cro: '',
  main_complaint: '',
  unit: effectiveUnitId || '',
  dentition: '',
  soft_tissue: '',
  gums_and_periodontium: '',
  edema: '',
  tongue: '',
  mobility: '',
  oral_hygiene: '',
  other_problems: '',
  file: null,
  tooth_number: '',
  procedure: '',
  evolution: '',
  health_professional: userInfo?.id || '',
});

const ODONTOGRAM_ROWS = [
  [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
  [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
];

const normalizeListPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const formatDatePtBr = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};


const Odontology = () => {
  const location = useLocation();
  const { internalId } = location.state || {};
  const { userInfo, effectiveUnitId, requireWriteUnit } = useUsers();
  const {
    createOdontology,
    listOdontology,
    updateOdontology,
    addOdontologyEvolution,
    listOdontologyEvolution,
  } = useInternals();
  const { listDentitions, listProcedures } = useAuxiliaries();
  const { summaryListUnit } = useUnits();

  const [dentitions, setDentitions] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [formData, setFormData] = useState(() => initializeFormData(userInfo, effectiveUnitId));
  const [evolutionDate, setEvolutionDate] = useState(() => localToday());
  const [evolutions, setEvolutions] = useState([]);
  const [acolhimentos, setAcolhimentos] = useState([]);
  const [unitsMap, setUnitsMap] = useState({});
  const [showAcolhimentoModal, setShowAcolhimentoModal] = useState(false);
  const [internalDetails, setInternalDetails] = useState(null);
  const [currentTab, setCurrentTab] = useState('dados');
  const [isSelectingInternal, setIsSelectingInternal] = useState(false);
  const {
    isSubmitting: isSavingOdontology,
    runWithSubmitLock: runSaveOdontology,
  } = useSubmitLock();
  const {
    isSubmitting: isSavingEvolution,
    runWithSubmitLock: runSaveEvolution,
  } = useSubmitLock();

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      unit: effectiveUnitId || '',
    }));
  }, [effectiveUnitId]);

  // Função para carregar dados auxiliares
  const loadAuxiliaryData = useCallback(async () => {
    try {
      const [dentitionsData, proceduresData] = await Promise.all([
        listDentitions(),
        listProcedures(),
      ]);
      setDentitions(normalizeListPayload(dentitionsData));
      setProcedures(normalizeListPayload(proceduresData));
    } catch (error) {
      console.error('Erro ao carregar dados auxiliares:', error);
    }
  }, [listDentitions, listProcedures]);

  // Efeito para carregar nome/id das unidades (necessário para exibir onde cada acolhimento foi feito)
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        const response = await summaryListUnit();
        const units = Array.isArray(response?.message)
          ? response.message
          : (Array.isArray(response?.results) ? response.results : (Array.isArray(response) ? response : []));
        const map = {};
        units.forEach((unit) => { map[unit.id] = unit; });
        setUnitsMap(map);
      } catch (error) {
        console.error('Erro ao buscar unidades:', error);
        setUnitsMap({});
      }
    };
    fetchUnits();
  }, [summaryListUnit]);

  // Função para carregar a listagem de acolhimentos odontológicos do interno
  const loadAcolhimentos = useCallback(
    async (internalIdParam) => {
      try {
        const response = await listOdontology(internalIdParam);
        setAcolhimentos(response?.results || []);
      } catch (error) {
        console.error('Erro ao carregar acolhimentos:', error);
      }
    },
    [listOdontology]
  );

  // Função para selecionar um interno

  const handleInternalSelect = useCallback(
    async (internal) => {
      try {
        setInternalDetails(internal);
        setFormData({ ...initializeFormData(userInfo, effectiveUnitId), internal: internal.id });

        await loadAcolhimentos(internal.id);

        const evolutionResponse = await listOdontologyEvolution(internal.id);
        setEvolutions(evolutionResponse?.results || []);
      } catch (error) {
        console.error('Erro ao carregar dados do interno:', error);
        toast.error('Erro ao carregar dados do interno.');
      }
    },
    [loadAcolhimentos, listOdontologyEvolution, userInfo, effectiveUnitId]
  );

  // Efeito para carregar dados auxiliares
  useEffect(() => {
    if (effectiveUnitId !== null && effectiveUnitId !== undefined && String(effectiveUnitId) !== '') {
      loadAuxiliaryData();
    }
  }, [loadAuxiliaryData, effectiveUnitId]);


  useEffect(() => {
    if (internalId) {
      const fetchInternalById = async () => {
        try {
          const internalsResponse = await listOdontology(internalId);
          const internalsData = internalsResponse?.results?.[0];

          if (internalsData) {
            handleInternalSelect({ id: internalId });
          }
        } catch (error) {
          console.error("Erro ao carregar interno pelo ID:", error);
        }
      };

      fetchInternalById();
    }
  }, [internalId, listOdontology, handleInternalSelect]);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === 'file' ? files[0] : value,
    }));
  };

  const handleToothSelect = (toothNumber) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      tooth_number: String(toothNumber),
    }));
  };

  const selectedToothNumber = String(formData.tooth_number || '').trim();

  const markedToothNumbers = useMemo(() => {
    const numbers = new Set();
    (evolutions || []).forEach((evo) => {
      const value = String(evo?.tooth_number || '').trim();
      if (value) {
        numbers.add(value);
      }
    });
    return numbers;
  }, [evolutions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runSaveOdontology(async () => {
      const writeUnit = requireWriteUnit();
      if (!writeUnit.ok) {
        toast.warning(writeUnit.message);
        return;
      }

      if (!formData.internal) {
        toast.error('Nenhum interno selecionado.');
        return;
      }

      if (!formData.dentition) {
        toast.warning('Selecione a dentição.');
        return;
      }

      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'file' && value) {
          data.append(key, value);
        } else if (key === 'unit') {
          data.append(key, writeUnit.unitId);
        } else if (key === 'other_problems') {
          data.append(key, value || 'N/A');
        } else if (value) {
          data.append(key, value);
        }
      });

      const result = formData.id
        ? await updateOdontology(formData.id, data)
        : await createOdontology(data);

      if (!result) return;

      setShowAcolhimentoModal(false);
      await loadAcolhimentos(formData.internal);
    });
  };

  const handleOpenNewAcolhimento = () => {
    setFormData((prevFormData) => ({
      ...initializeFormData(userInfo, effectiveUnitId),
      internal: prevFormData.internal,
    }));
    setShowAcolhimentoModal(true);
  };

  const handleOpenEditAcolhimento = (acolhimento) => {
    const normalizedDentition =
      typeof acolhimento?.dentition === 'object'
        ? (acolhimento?.dentition?.id || '')
        : (acolhimento?.dentition || '');

    setFormData((prevFormData) => ({
      ...acolhimento,
      dentition: normalizedDentition,
      internal: prevFormData.internal,
      file: null,
    }));
    setShowAcolhimentoModal(true);
  };

  const handleEvolutionSubmit = async () => {
    await runSaveEvolution(async () => {
      if (!formData.internal) {
        toast.error('Nenhum interno selecionado.');
        return;
      }

      if (!selectedToothNumber) {
        toast.warning('Selecione um dente no odontograma.');
        return;
      }

      if (!formData.procedure) {
        toast.warning('Selecione o procedimento.');
        return;
      }

      const data = new FormData();
      data.append('internal', formData.internal);
      data.append('date', evolutionDate);
      data.append('tooth_number', selectedToothNumber);
      data.append('procedure', formData.procedure);
      data.append('health_professional', formData.health_professional);

      if (formData.file) {
        data.append('file', formData.file);
      }

      try {
        await addOdontologyEvolution(data);
        const evolutionResponse = await listOdontologyEvolution(formData.internal);
        setEvolutions(evolutionResponse?.results || []);
        setFormData((prevFormData) => ({
          ...prevFormData,
          procedure: '',
          file: null,
        }));
      } catch (error) {
        console.error('Erro ao adicionar evolução:', error);
      }
    });
  };


  const renderAcolhimentoForm = () => (
    <div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Dentista</label>
          <input type="text" name="dentist" value={formData.dentist || ''} onChange={handleInputChange} />
        </div>
        <div className='social-form-group'>
          <label>CRO do Dentista</label>
          <input type="text" name="dentist_cro" value={formData.dentist_cro || ''} onChange={handleInputChange} />
        </div>
      </div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Assistente</label>
          <input type="text" name="assistant" value={formData.assistant || ''} onChange={handleInputChange} />
        </div>
        <div className='social-form-group'>
          <label>CRO do Assistente</label>
          <input type="text" name="assistant_cro" value={formData.assistant_cro || ''} onChange={handleInputChange} />
        </div>
      </div>
      <div className='social-form-group'>
        <label>Dentição</label>
        <select name="dentition" value={formData.dentition || ''} onChange={handleInputChange} required>
          <option value="">Selecione</option>
          {dentitions.map((dentition) => (
            <option key={dentition.id} value={dentition.id || ''}>{dentition.name}</option>
          ))}
        </select>
      </div>
      <div className='social-form-group textarea-full'>
        <label>Queixa Principal</label>
        <textarea rows={3} name="main_complaint" value={formData.main_complaint || ''} onChange={handleInputChange} />
      </div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Tecido Mole</label>
          <input type="text" name="soft_tissue" value={formData.soft_tissue || ''} onChange={handleInputChange} />
        </div>
        <div className='social-form-group'>
          <label>Gengivas e Periodonto</label>
          <input type="text" name="gums_and_periodontium" value={formData.gums_and_periodontium || ''} onChange={handleInputChange} />
        </div>
      </div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Edema</label>
          <input type="text" name="edema" value={formData.edema || ''} onChange={handleInputChange} />
        </div>
        <div className='social-form-group'>
          <label>Língua</label>
          <input type="text" name="tongue" value={formData.tongue || ''} onChange={handleInputChange} />
        </div>
      </div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Mobilidade</label>
          <input type="text" name="mobility" value={formData.mobility || ''} onChange={handleInputChange} />
        </div>
        <div className='social-form-group'>
          <label>Higiene Oral</label>
          <input type="text" name="oral_hygiene" value={formData.oral_hygiene || ''} onChange={handleInputChange} />
        </div>
      </div>
      <div className='social-form-group textarea-full'>
        <label>Outros Problemas</label>
        <textarea rows={3} name="other_problems" value={formData.other_problems || ''} onChange={handleInputChange} />
      </div>

      <div>
        <button className="social-submit-button" onClick={handleSubmit} disabled={isSavingOdontology}>
          {isSavingOdontology ? 'Enviando...' : 'Enviar'}
        </button>
      </div>
    </div>
  );

  const renderAcolhimentoTab = () => (
    <div className="timeline">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Acolhimentos</h4>
        <button className="social-submit-button" onClick={handleOpenNewAcolhimento}>
          <FontAwesomeIcon icon={faPlus} /> Adicionar Acolhimento
        </button>
      </div>

      {acolhimentos.length > 0 ? (
        <ul className="timeline-list">
          {acolhimentos.map((item) => {
            const unitId = item?.unit?.id ?? item?.unit;
            const unitFromMap = unitsMap[unitId];
            const unitName =
              item.unit?.name ||
              item.unit?.acronym ||
              unitFromMap?.name ||
              unitFromMap?.acronym ||
              (unitId ? `Unidade ${unitId}` : 'Não informada');

            const dentitionId = typeof item.dentition === 'object' ? item.dentition?.id : item.dentition;
            const dentitionName =
              item.dentition?.name ||
              dentitions.find((d) => String(d.id) === String(dentitionId))?.name ||
              (dentitionId || '-');

            return (
              <li key={item.id} className="timeline-item">
                <div className="timeline-date">
                  {formatDatePtBr(item.date)}
                  <button
                    type="button"
                    className="timeline-edit-btn"
                    onClick={() => handleOpenEditAcolhimento(item)}
                    title="Editar acolhimento"
                  >
                    <FontAwesomeIcon icon={faPencil} />
                  </button>
                </div>
                <div className="timeline-unit">{unitName}</div>
                <div className="timeline-content">
                  <p><strong>Dentista:</strong> {item.dentist || '-'}{item.dentist_cro ? ` (CRO ${item.dentist_cro})` : ''}</p>
                  <p><strong>Assistente:</strong> {item.assistant || '-'}{item.assistant_cro ? ` (CRO ${item.assistant_cro})` : ''}</p>
                  <p><strong>Dentição:</strong> {dentitionName}</p>
                  <p><strong>Queixa Principal:</strong> {item.main_complaint || '-'}</p>
                  <p><strong>Tecido Mole:</strong> {item.soft_tissue || '-'}</p>
                  <p><strong>Gengivas e Periodonto:</strong> {item.gums_and_periodontium || '-'}</p>
                  <p><strong>Edema:</strong> {item.edema || '-'}</p>
                  <p><strong>Língua:</strong> {item.tongue || '-'}</p>
                  <p><strong>Mobilidade:</strong> {item.mobility || '-'}</p>
                  <p><strong>Higiene Oral:</strong> {item.oral_hygiene || '-'}</p>
                  <p><strong>Outros Problemas:</strong> {item.other_problems || '-'}</p>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p>Nenhum acolhimento registrado.</p>
      )}
    </div>
  );

  const renderEvolucaoTab = () => (
    <div className="evolucao-container">
      <div className='social-form-group'>
        <label>Odontograma</label>
        <div className="odontogram-card">
          <p className="odontogram-help">Clique no dente para selecionar.</p>
          {ODONTOGRAM_ROWS.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className={`odontogram-row ${rowIndex === 0 ? 'upper' : 'lower'}`}>
              {row.map((toothNumber, toothIndex) => {
                const toothValue = String(toothNumber);
                const isMarked = markedToothNumbers.has(toothValue);
                const isSelected = selectedToothNumber === toothValue;
                const centerIndex = (row.length - 1) / 2;
                const distanceFromCenter = Math.abs(toothIndex - centerIndex) / centerIndex;
                const curveIntensity = Math.pow(distanceFromCenter, 1.8) * 14;
                const arcDirection = rowIndex === 0 ? -1 : 1;
                return (
                  <button
                    key={toothNumber}
                    type="button"
                    className={`odontogram-tooth${isMarked ? ' marked' : ''}${isSelected ? ' active' : ''}`}
                    onClick={() => handleToothSelect(toothNumber)}
                    title={`Dente ${toothNumber}`}
                    style={{ '--tooth-shift': `${Math.round(curveIntensity * arcDirection)}px` }}
                  >
                    {toothNumber}
                  </button>
                );
              })}
            </div>
          ))}
          <div className="odontogram-legend">
            <span><i className="legend-dot selected" /> Selecionado</span>
            <span><i className="legend-dot marked" /> Já registrado</span>
          </div>
        </div>
      </div>
      <div className='social-form-group-duplo'>
        <div className='social-form-group'>
          <label>Procedimento</label>
          <select
            name="procedure"
            value={formData.procedure || ''}
            onChange={handleInputChange}
          >
            <option value="">Selecione</option>
            {procedures.map((procedure) => (
              <option key={procedure.id} value={procedure.id}>{procedure.name}</option>
            ))}
          </select>
        </div>
        <div className='social-form-group'>
          <label>Data</label>
          <input
            type="date"
            value={evolutionDate}
            onChange={(e) => setEvolutionDate(e.target.value)}
          />
        </div>
      </div>
      <div>
        <button className="social-submit-button" onClick={handleEvolutionSubmit} disabled={isSavingEvolution}>
          {isSavingEvolution ? 'Salvando...' : 'Adicionar Evolução'}
        </button>
      </div>

      <div className="timeline">
        <h4>Evoluções</h4>
        <ul className="timeline-list">
          {evolutions.length > 0 ? (
            evolutions.map((evo, index) => {
              const procedureName = procedures.find(
                (proc) => String(proc.id) === String(evo.procedure)
              )?.name || evo?.procedure?.name || evo.procedure;
              return (
                <li key={index} className="timeline-item">
                  <div className="timeline-date">
                    <FontAwesomeIcon icon={faClock} /> {formatDatePtBr(evo.date)}
                  </div>
                  <div className="timeline-content">
                    <p><strong>Dente:</strong> {evo.tooth_number}</p>
                    <p><strong>Procedimento:</strong> {procedureName}</p>
                  </div>
                </li>
              );
            })
          ) : (
            <p>Nenhuma evolução registrada.</p>
          )}
        </ul>
      </div>
    </div>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className='container social-container admission-page'>
        <Titulo
          children={
            <SearchInternal
              onSelect={handleInternalSelect}
              onSelectingChange={setIsSelectingInternal}
            />
          }

        />

        <PageHeader
          title="Odontologia"
          subtitle="Acompanhamento odontológico do interno."
          internalDetails={internalDetails}
        />

        {isSelectingInternal ? (
          <InternalDataSkeleton />
        ) : (
          <>
        <div className='social-tabs'>
          <button className={`social-tab-button ${currentTab === 'dados' ? 'active' : ''}`} onClick={() => setCurrentTab('dados')}>Acolhimento</button>
          <button className={`social-tab-button ${currentTab === 'evolucao' ? 'active' : ''}`} onClick={() => setCurrentTab('evolucao')}>Evolução</button>
        </div>

        <div className='social-tab-content'>
          {currentTab === 'dados' ? renderAcolhimentoTab() : renderEvolucaoTab()}
        </div>
          </>
        )}
      </div>

      <Modal
        isOpen={showAcolhimentoModal}
        closeModal={() => setShowAcolhimentoModal(false)}
        title={formData.id ? 'Editar Acolhimento' : 'Novo Acolhimento'}
      >
        {renderAcolhimentoForm()}
      </Modal>
    </div>
  );
};

export default Odontology;
